import { Controller } from "@hotwired/stimulus"

type DragHandlers = Record<"dragstart" | "dragover" | "dragleave" | "drop" | "dragend", (e: DragEvent) => void>

export default class extends Controller<HTMLElement> {
  static targets = ["item", "status"]
  static values  = { url: String }

  declare readonly itemTargets: HTMLElement[]
  declare readonly hasStatusTarget: boolean
  declare readonly statusTarget: HTMLElement
  declare readonly urlValue: string

  private _dragged: HTMLElement | null = null
  private _handlers!: DragHandlers

  connect() {
    this._dragged = null
    this._handlers = {
      dragstart: this._onDragStart.bind(this),
      dragover:  this._onDragOver.bind(this),
      dragleave: this._onDragLeave.bind(this),
      drop:      this._onDrop.bind(this),
      dragend:   this._onDragEnd.bind(this)
    }
    for (const [type, handler] of Object.entries(this._handlers)) {
      this.element.addEventListener(type, handler as EventListener)
    }
  }

  disconnect() {
    for (const [type, handler] of Object.entries(this._handlers)) {
      this.element.removeEventListener(type, handler as EventListener)
    }
    this._dragged = null
  }

  _onDragStart(e: DragEvent) {
    const item = this._itemAt(e)
    if (!item) return
    this._dragged = item
    item.style.opacity = "0.4"
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move"
  }

  _onDragOver(e: DragEvent) {
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move"
    const item = this._itemAt(e)
    if (!item || item === this._dragged) return
    this._clearHighlights()
    item.style.borderTop = "2px solid var(--accent)"
  }

  _onDragLeave(e: DragEvent) {
    const item = this._itemAt(e)
    if (item) item.style.borderTop = ""
  }

  _onDrop(e: DragEvent) {
    e.preventDefault()
    const target = this._itemAt(e)
    if (!target || !this._dragged || target === this._dragged) return
    this._clearHighlights()

    const items = [...this.element.querySelectorAll<HTMLElement>("[data-oeuvres-order-target='item']")]
    const fromIdx = items.indexOf(this._dragged)
    const toIdx   = items.indexOf(target)
    const previousOrder = items

    if (fromIdx < toIdx) {
      target.after(this._dragged)
    } else {
      target.before(this._dragged)
    }

    this._saveOrder(previousOrder)
  }

  _onDragEnd() {
    if (this._dragged) this._dragged.style.opacity = ""
    this._clearHighlights()
    this._dragged = null
  }

  _itemAt(e: DragEvent): HTMLElement | null {
    return (e.target as Element | null)?.closest<HTMLElement>("[data-oeuvres-order-target='item']") ?? null
  }

  _clearHighlights() {
    this.itemTargets.forEach(item => { item.style.borderTop = "" })
  }

  // On failure the rows go back to the saved order, so the screen never shows an order the server didn't keep
  _saveOrder(previousOrder: HTMLElement[]) {
    const ids = this.itemTargets.map(item => item.dataset.id)
    const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || ""
      },
      body: JSON.stringify({ ids })
    }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      this._updatePositionBadges()
      this._announce("Ordre d'accrochage enregistré.")
    }).catch(err => {
      console.error("Reorder failed", err)
      previousOrder.forEach(item => this.element.querySelector("ul")?.append(item))
      this._announce("Impossible d'enregistrer l'ordre : l'ancien ordre est rétabli. Rechargez la page puis réessayez.")
    })
  }

  _announce(message: string) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  _updatePositionBadges() {
    this.itemTargets.forEach((item, i) => {
      const badge = item.querySelector("[data-position-badge]")
      if (badge) badge.textContent = `#${i + 1}`
    })
  }
}
